import { Link } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import TeacherShell, { TeacherPageHeader } from '../../../components/layout/TeacherShell';
import { EmptyState } from '../../../components/layout/AppShell';
import { Card, Button } from '../../../components/ui';
import { cn } from '../../../lib/cn';

export default function CoursesIndex({ courses }) {
  const { t } = useTranslation();
  return (
    <TeacherShell scrollTitle={t('teacherCourses.title')}>
      <TeacherPageHeader
        title={t('teacherCourses.title')}
        subtitle={t('teacherCourses.subtitle')}
        actions={
          <div className="flex items-center gap-3">
            <Link href="/teacher/activities/new">
              <Button variant="secondary" size="sm">
                {t('teacherCourses.startNow')}
                <svg className="w-4 h-4 ml-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Button>
            </Link>
            <Link href="/teacher/courses/new">
              <Button>{t('teacherCourses.newCourse')}</Button>
            </Link>
          </div>
        }
      />

      {courses.length === 0 ? (
        <EmptyState
          title={t('teacherCourses.noCourses')}
          description={t('teacherCourses.noCoursesHelp')}
          action={
            <Link href="/teacher/courses/new">
              <Button>{t('teacherCourses.createCourse')}</Button>
            </Link>
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Link href="/teacher/courses/new" className="block">
            <Card className="p-5 h-full border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 transition-colors flex items-center justify-center min-h-[140px]">
              <div className="text-center">
                <svg className="w-8 h-8 mx-auto text-gray-400 dark:text-gray-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <p className="font-medium text-gray-500 dark:text-gray-400">{t('teacherCourses.newCourse')}</p>
              </div>
            </Card>
          </Link>
          {courses.map((course) => (
            <Link key={course.id} href={`/teacher/courses/${course.id}`} className="block">
              <Card className="p-5 hover:shadow-md transition-shadow h-full">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {course.title}
                    </h3>
                    {course.description && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                        {course.description}
                      </p>
                    )}
                    <div className="flex items-center gap-4 mt-3 text-xs text-gray-500 dark:text-gray-400">
                      <span>{course.unit_count} {course.unit_count === 1 ? t('common.unit') : t('common.units')}</span>
                      <span>{course.student_count} {course.student_count === 1 ? t('common.student') : t('common.students')}</span>
                    </div>
                  </div>
                  <span
                    className={cn(
                      'text-xs font-medium px-2 py-0.5 rounded',
                      course.status === 'active'
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : course.status === 'archived'
                          ? 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                          : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                    )}
                  >
                    {course.status}
                  </span>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </TeacherShell>
  );
}
